/**
 * Signup.jsx — Page d'inscription client
 *
 * Étape 1 : formulaire d'informations personnelles
 * Étape 2 : selfie obligatoire (caméra avant, cadre ovale de guidage)
 * Submit   : envoi JSON avec photoDataUrl inclus
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiCamera, FiCheck, FiEye, FiEyeOff, FiBriefcase, FiCheckCircle, FiArrowRight, FiZap, FiRefreshCw, FiX } from 'react-icons/fi';
import Navbar from '../shared/Navbar.jsx';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import '../home/Home.css';
import './Signup.css';

const apiBaseUrl = getApiBaseUrl();

/* ── Overlay ovale pour cadrer le visage ───────────────────────────────────── */
function FaceOverlay() {
	return (
		<svg
			style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
			viewBox="0 0 460 340"
			preserveAspectRatio="xMidYMid slice"
			xmlns="http://www.w3.org/2000/svg"
		>
			<defs>
				<mask id="su-face-mask">
					<rect width="460" height="340" fill="white" />
					<ellipse cx="230" cy="162" rx="105" ry="132" fill="black" />
				</mask>
			</defs>
			<rect width="460" height="340" fill="rgba(0,0,0,0.60)" mask="url(#su-face-mask)" />
			<ellipse cx="230" cy="162" rx="105" ry="132" fill="none"
				stroke="#4d9fff" strokeWidth="2.5" strokeDasharray="10 5" />
		</svg>
	);
}

function Signup() {
	const navigate = useNavigate();

	// ── Étape du wizard ──────────────────────────────────────────────────────
	const [step, setStep] = useState('form'); // 'form' | 'camera'

	// ── État du formulaire ───────────────────────────────────────────────────
	const [prenom, setPrenom] = useState('');
	const [nom, setNom] = useState('');
	const [email, setEmail] = useState('');
	const [dateNaissance, setDateNaissance] = useState('');
	const [indicatif, setIndicatif] = useState('+212');
	const [telephone, setTelephone] = useState('');
	const [usageLocation, setUsageLocation] = useState('');
	const [ville, setVille] = useState('');
	const [sexe, setSexe] = useState('');
	const [motDePasse, setMotDePasse] = useState('');
	const [confirmation, setConfirmation] = useState('');
	const [cguAcceptees, setCguAcceptees] = useState(false);
	const [chargement, setChargement] = useState(false);
	const [erreur, setErreur] = useState('');
	const [afficherMdp, setAfficherMdp] = useState(false);
	const [afficherConfirm, setAfficherConfirm] = useState(false);

	// ── État caméra selfie ───────────────────────────────────────────────────
	const [cameraOpen, setCameraOpen] = useState(false);
	const [facingMode, setFacingMode] = useState('user');
	const [capturedPhoto, setCapturedPhoto] = useState(null); // { previewUrl, dataUrl }
	const [cameraError, setCameraError] = useState('');
	const streamRef = useRef(null);
	const videoRef = useRef(null);

	/* Assigne srcObject après que <video> soit monté dans le DOM */
	useEffect(() => {
		if (cameraOpen && videoRef.current && streamRef.current) {
			videoRef.current.srcObject = streamRef.current;
			videoRef.current.play().catch(() => {});
		}
	}, [cameraOpen]);

	useEffect(() => () => stopStream(), []);

	const stopStream = () => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach(t => t.stop());
			streamRef.current = null;
		}
	};

	const openCamera = async (mode = facingMode) => {
		setCameraError('');
		stopStream();
		if (!navigator.mediaDevices?.getUserMedia) {
			setCameraError('La caméra n\'est pas disponible sur ce navigateur.');
			return;
		}
		try {
			const s = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: { ideal: mode }, width: { ideal: 1280 }, height: { ideal: 720 } },
				audio: false,
			});
			streamRef.current = s;
			setCameraOpen(true);
		} catch {
			setCameraError('Impossible d\'accéder à la caméra. Vérifiez les permissions.');
		}
	};

	const closeCamera = () => {
		stopStream();
		setCameraOpen(false);
	};

	const flipCamera = async () => {
		const newMode = facingMode === 'user' ? 'environment' : 'user';
		setFacingMode(newMode);
		if (cameraOpen) {
			stopStream();
			setCameraOpen(false);
			await openCamera(newMode);
		}
	};

	const handleCapture = () => {
		const video = videoRef.current;
		if (!video || video.readyState < 2) {
			setCameraError('La caméra n\'est pas encore prête, patientez.');
			return;
		}
		const canvas = document.createElement('canvas');
		canvas.width = video.videoWidth || 640;
		canvas.height = video.videoHeight || 480;
		canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
		const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
		setCapturedPhoto({ previewUrl: dataUrl, dataUrl });
		closeCamera();
	};

	// ── Validation formulaire → passe à l'étape caméra ─────────────────────
	const handleFormNext = (e) => {
		e.preventDefault();
		setErreur('');

		if (!dateNaissance || !indicatif || !telephone || !usageLocation || !ville || !sexe) {
			setErreur('Veuillez renseigner toutes les informations personnelles demandées.');
			return;
		}
		const naiss = new Date(dateNaissance);
		if (Number.isNaN(naiss.getTime()) || naiss > new Date()) {
			setErreur('Veuillez saisir une date de naissance valide.');
			return;
		}
		if (telephone.length < 6 || telephone.length > 14) {
			setErreur('Le numéro de téléphone doit contenir entre 6 et 14 chiffres.');
			return;
		}
		if (motDePasse !== confirmation) {
			setErreur('Les mots de passe ne correspondent pas.');
			return;
		}
		if (!cguAcceptees) {
			setErreur("Vous devez accepter les conditions d'utilisation.");
			return;
		}
		setStep('camera');
	};

	// ── Soumission finale (formulaire + photo) ───────────────────────────────
	const handleSubmit = async () => {
		if (!capturedPhoto) return;
		setErreur('');
		setChargement(true);
		try {
			const res = await fetch(`${apiBaseUrl}/api/users/signup`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					firstName: prenom,
					lastName: nom,
					email,
					birthDate: dateNaissance,
					phoneCountryCode: indicatif,
					phoneNumber: telephone,
					phoneFull: `${indicatif}${telephone}`,
					rentalUsage: usageLocation,
					city: ville,
					gender: sexe,
					password: motDePasse,
					confirmPassword: confirmation,
					termsAccepted: cguAcceptees,
					photoDataUrl: capturedPhoto.dataUrl,
				}),
			});

			const typeContenu = res.headers.get('content-type') || '';
			const data = typeContenu.includes('application/json')
				? await res.json()
				: { success: false, message: 'Réponse invalide du serveur.' };

			if (!res.ok || !data.success) {
				setErreur(data.message || "Échec de l'inscription.");
				setStep('form');
				return;
			}

			if (data.requiresVerification) {
				navigate(`/verify-email?email=${encodeURIComponent(data.email || email)}`);
				return;
			}

			window.localStorage.setItem('exp-auth-user', JSON.stringify(data.user));
			window.localStorage.setItem('exp-user-profile', JSON.stringify(data.user));
			navigate('/');
		} catch {
			setErreur('Impossible de contacter le serveur.');
			setStep('form');
		} finally {
			setChargement(false);
		}
	};

	const syne = { fontFamily: "'Syne', sans-serif" };
	const mono = { fontFamily: "'DM Mono', monospace" };

	return (
		<div className="vx-signup-page">
			<Navbar minimal />

			<main className="vx-signup-main">

				{/* ── Panneau gauche ── */}
				<div className="vx-signup-left">
					<div className="vx-signup-left-inner">
						<span className="vx-signup-kicker">
							<FiZap size={13} aria-hidden="true" />
							Nouveau compte
						</span>
						<h1 className="vx-signup-title">
							Rejoignez<br /><em>la flotte.</em>
						</h1>
						<p className="vx-signup-subtitle">
							Créez votre compte UltimateX Drive et accédez à une expérience de location premium.
						</p>
						<div className="vx-signup-req-block">
							<p className="vx-signup-req-title">Conditions pour louer</p>
							<p className="vx-signup-req-text">
								Pour valider une réservation, votre compte devra disposer d&apos;un permis de plus de 2 ans
								et d&apos;une vérification d&apos;identité.
							</p>
							<ul className="vx-signup-req-list">
								<li><FiCheckCircle size={13} aria-hidden="true" /> Permis valide — ancienneté supérieure à 2 ans</li>
								<li><FiCheckCircle size={13} aria-hidden="true" /> Vérification du permis de conduire</li>
								<li><FiCheckCircle size={13} aria-hidden="true" /> Vérification d&apos;identité sécurisée</li>
							</ul>
						</div>
					</div>
				</div>

				{/* ── Panneau droit ── */}
				<div className="vx-signup-right">
					<div className="vx-signup-right-inner">

						{/* ── Indicateur d'étape ── */}
						<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem' }}>
							{['form', 'camera'].map((s, i) => (
								<div key={s} style={{
									height: 6, width: s === step ? 28 : 14,
									borderRadius: 3,
									background: (step === 'camera' && s === 'form') ? '#10b981'
										: s === step ? '#1a56f0' : '#dde2ee',
									transition: 'all 0.3s ease',
								}} />
							))}
							<span style={{ ...mono, fontSize: '0.62rem', color: '#9aa0af', letterSpacing: '0.08em', marginLeft: 4 }}>
								{step === 'form' ? '1 / 2' : '2 / 2'}
							</span>
						</div>

						{/* ════════════════════════════════════════════
						    ÉTAPE 1 — Formulaire
						════════════════════════════════════════════ */}
						{step === 'form' && (
							<>
								<div className="vx-signup-form-head">
									<span className="vx-section-kicker">Inscription</span>
									<h2 className="vx-signup-form-title">Créez votre compte</h2>
									<p className="vx-signup-form-sub">Remplissez les informations ci-dessous pour rejoindre la plateforme.</p>
								</div>

								<form className="vx-signup-card" onSubmit={handleFormNext} noValidate>

									<div className="vx-signup-row">
										<div className="vx-signup-field">
											<label className="vx-signup-label" htmlFor="vx-signup-firstname">Prénom</label>
											<input id="vx-signup-firstname" type="text" className="vx-signup-input"
												placeholder="Jean" value={prenom} onChange={(e) => setPrenom(e.target.value)} required />
										</div>
										<div className="vx-signup-field">
											<label className="vx-signup-label" htmlFor="vx-signup-lastname">Nom</label>
											<input id="vx-signup-lastname" type="text" className="vx-signup-input"
												placeholder="Dupont" value={nom} onChange={(e) => setNom(e.target.value)} required />
										</div>
									</div>

									<div className="vx-signup-field">
										<label className="vx-signup-label" htmlFor="vx-signup-email">Adresse e-mail</label>
										<input id="vx-signup-email" type="email" className="vx-signup-input"
											placeholder="vous@ultimatexdrive.com" value={email}
											onChange={(e) => setEmail(e.target.value)} required />
									</div>

									<div className="vx-signup-row">
										<div className="vx-signup-field">
											<label className="vx-signup-label" htmlFor="vx-signup-birthdate">Date de naissance</label>
											<input id="vx-signup-birthdate" type="date" className="vx-signup-input"
												value={dateNaissance} onChange={(e) => setDateNaissance(e.target.value)} required />
										</div>
										<div className="vx-signup-field">
											<label className="vx-signup-label" htmlFor="vx-signup-gender">Sexe</label>
											<select id="vx-signup-gender" className="vx-signup-input" value={sexe}
												onChange={(e) => setSexe(e.target.value)} required>
												<option value="">Sélectionnez</option>
												<option value="Homme">Homme</option>
												<option value="Femme">Femme</option>
												<option value="Autre">Autre</option>
											</select>
										</div>
									</div>

									<div className="vx-signup-row">
										<div className="vx-signup-field">
											<label className="vx-signup-label" htmlFor="vx-signup-code">Indicatif</label>
											<select id="vx-signup-code" className="vx-signup-input" value={indicatif}
												onChange={(e) => setIndicatif(e.target.value)} required>
												<option value="+212">Maroc (+212)</option>
												<option value="+33">France (+33)</option>
												<option value="+213">Algérie (+213)</option>
												<option value="+216">Tunisie (+216)</option>
												<option value="+32">Belgique (+32)</option>
												<option value="+1">USA/Canada (+1)</option>
												<option value="+34">Espagne (+34)</option>
												<option value="+49">Allemagne (+49)</option>
											</select>
										</div>
										<div className="vx-signup-field">
											<label className="vx-signup-label" htmlFor="vx-signup-phone">Téléphone</label>
											<input id="vx-signup-phone" type="tel" className="vx-signup-input"
												placeholder="612345678" value={telephone}
												onChange={(e) => setTelephone(e.target.value.replace(/\D/g, ''))}
												inputMode="numeric" pattern="[0-9]{6,14}" required />
										</div>
									</div>

									<div className="vx-signup-field">
										<p className="vx-signup-label">Vous allez utiliser l&apos;application pour louer :</p>
										<div className="vx-signup-radio-group" role="radiogroup">
											<label className="vx-signup-radio-option" htmlFor="vx-rental-self">
												<input id="vx-rental-self" type="radio" name="vx-rental"
													value="self" checked={usageLocation === 'self'}
													onChange={(e) => setUsageLocation(e.target.value)} required />
												Pour moi
											</label>
											<label className="vx-signup-radio-option" htmlFor="vx-rental-other">
												<input id="vx-rental-other" type="radio" name="vx-rental"
													value="other" checked={usageLocation === 'other'}
													onChange={(e) => setUsageLocation(e.target.value)} />
												Pour une autre personne
											</label>
										</div>
										{usageLocation === 'other' && (
											<p className="vx-signup-rental-note">
												Le permis de conduire de la personne concernée sera demandé lors de la réservation.
											</p>
										)}
									</div>

									<div className="vx-signup-field">
										<label className="vx-signup-label" htmlFor="vx-signup-city">Ville</label>
										<input id="vx-signup-city" type="text" className="vx-signup-input"
											placeholder="Casablanca" value={ville}
											onChange={(e) => setVille(e.target.value)} required />
									</div>

									<div className="vx-signup-field">
										<label className="vx-signup-label" htmlFor="vx-signup-password">Mot de passe</label>
										<div className="vx-signup-pw-wrap">
											<input id="vx-signup-password" type={afficherMdp ? 'text' : 'password'}
												className="vx-signup-input" placeholder="Minimum 8 caractères"
												value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} required />
											<button type="button" className="vx-signup-pw-toggle"
												onClick={() => setAfficherMdp((v) => !v)}
												aria-label={afficherMdp ? 'Masquer' : 'Afficher'}>
												{afficherMdp ? <FiEyeOff size={16} /> : <FiEye size={16} />}
											</button>
										</div>
									</div>

									<div className="vx-signup-field">
										<label className="vx-signup-label" htmlFor="vx-signup-confirm">Confirmer le mot de passe</label>
										<div className="vx-signup-pw-wrap">
											<input id="vx-signup-confirm" type={afficherConfirm ? 'text' : 'password'}
												className="vx-signup-input" placeholder="Confirmez votre mot de passe"
												value={confirmation} onChange={(e) => setConfirmation(e.target.value)} required />
											<button type="button" className="vx-signup-pw-toggle"
												onClick={() => setAfficherConfirm((v) => !v)}
												aria-label={afficherConfirm ? 'Masquer' : 'Afficher'}>
												{afficherConfirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
											</button>
										</div>
									</div>

									<label className="vx-signup-terms">
										<input type="checkbox" checked={cguAcceptees}
											onChange={(e) => setCguAcceptees(e.target.checked)} required />
										<span className="vx-signup-terms-text">
											J&apos;accepte les <a href="#terms">conditions d&apos;utilisation</a> et la{' '}
											<a href="#privacy">politique de confidentialité</a>
										</span>
									</label>

									{erreur && <p className="vx-signup-error" role="alert">⚠ {erreur}</p>}

									<button type="submit" className="vx-btn vx-btn-solid vx-signup-submit"
										disabled={!cguAcceptees}>
										Suivant — Photo de profil <FiArrowRight size={14} aria-hidden="true" />
									</button>

									<div className="vx-signup-agency" role="note">
										<p className="vx-signup-agency-title">
											<FiBriefcase size={14} aria-hidden="true" />
											Vous êtes une agence ?
										</p>
										<p className="vx-signup-agency-text">
											Rejoignez notre réseau de partenaires et développez votre activité en ligne.
										</p>
										<Link to="/signupagence" className="vx-signup-agency-btn">
											Devenir partenaire <FiArrowRight size={13} aria-hidden="true" />
										</Link>
									</div>
								</form>

								<p className="vx-signup-footer">
									Déjà un compte ? <Link to="/login">Se connecter</Link>
								</p>
							</>
						)}

						{/* ════════════════════════════════════════════
						    ÉTAPE 2 — Selfie de profil
						════════════════════════════════════════════ */}
						{step === 'camera' && (
							<div style={{ width: '100%' }}>
								<div className="vx-signup-form-head">
									<span className="vx-section-kicker">Photo de profil</span>
									<h2 className="vx-signup-form-title">Votre selfie d'identité</h2>
									<p className="vx-signup-form-sub">
										Une photo instantanée est obligatoire. Elle sera validée par l'équipe UltimateX Drive avant activation complète de votre compte.
									</p>
								</div>

								{/* ── Aperçu photo capturée ── */}
								{capturedPhoto ? (
									<div style={{
										borderRadius: 16, overflow: 'hidden',
										border: '2.5px solid #10b981',
										boxShadow: '0 4px 20px rgba(16,185,129,0.2)',
										marginBottom: '1.2rem', position: 'relative',
									}}>
										<img src={capturedPhoto.previewUrl} alt="Aperçu du selfie"
											style={{ width: '100%', height: 240, objectFit: 'cover', display: 'block' }} />
										<div style={{
											position: 'absolute', bottom: 0, left: 0, right: 0,
											background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
											padding: '1.5rem 1rem 0.8rem',
											display: 'flex', justifyContent: 'space-between', alignItems: 'center',
										}}>
											<span style={{ color: '#fff', fontSize: '0.82rem', fontWeight: 600, ...syne, display: 'flex', alignItems: 'center', gap: 6 }}>
												<FiCheck size={14} color="#10b981" /> Photo capturée
											</span>
											<button onClick={() => { setCapturedPhoto(null); openCamera(); }} style={{
												...syne, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
												borderRadius: 8, padding: '4px 12px', color: '#fff', cursor: 'pointer',
												fontSize: '0.72rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5,
											}}>
												<FiCamera size={11} /> Reprendre
											</button>
										</div>
									</div>
								) : (
									/* ── Bouton ouvrir caméra ── */
									<button onClick={() => openCamera()} style={{
										width: '100%', padding: '1rem',
										display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.65rem',
										background: 'linear-gradient(135deg, #1a56f0, #0f3fd4)',
										color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer',
										...syne, fontSize: '0.9rem', fontWeight: 700,
										boxShadow: '0 6px 20px rgba(26,86,240,0.38)',
										marginBottom: '1.2rem',
									}}>
										<FiCamera size={18} /> Ouvrir la caméra
									</button>
								)}

								{cameraError && (
									<p style={{ fontSize: '0.77rem', color: '#dc2626', marginBottom: '0.8rem', ...mono }}>
										{cameraError}
									</p>
								)}

								{erreur && <p className="vx-signup-error" role="alert">⚠ {erreur}</p>}

								<div style={{ display: 'flex', gap: '0.7rem' }}>
									<button onClick={() => { closeCamera(); setStep('form'); }} style={{
										...syne, flex: '0 0 auto', padding: '0.72rem 1.1rem',
										background: 'none', border: '1.5px solid #dde2ee',
										borderRadius: 10, cursor: 'pointer', color: '#5a6478',
										fontSize: '0.82rem', fontWeight: 600,
									}}>
										← Retour
									</button>
									<button
										onClick={handleSubmit}
										disabled={!capturedPhoto || chargement}
										style={{
											...syne, flex: 1, padding: '0.75rem',
											background: capturedPhoto && !chargement
												? 'linear-gradient(135deg, #10b981, #059669)'
												: '#e8ecf5',
											color: capturedPhoto && !chargement ? '#fff' : '#aab0c0',
											border: 'none', borderRadius: 10,
											cursor: capturedPhoto && !chargement ? 'pointer' : 'not-allowed',
											fontSize: '0.88rem', fontWeight: 700,
											display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
										}}
									>
										{chargement ? 'Création en cours…' : <><FiCheck size={16} /> Créer mon compte</>}
									</button>
								</div>
							</div>
						)}

					</div>
				</div>
			</main>

			{/* ── Overlay caméra ── */}
			{cameraOpen && (
				<div style={{
					position: 'fixed', inset: 0, zIndex: 9999,
					background: 'rgba(0, 4, 18, 0.95)',
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					backdropFilter: 'blur(10px)', padding: '1rem',
				}}>
					<div style={{
						width: '100%', maxWidth: 490,
						background: '#0a0f1e',
						borderRadius: 20, overflow: 'hidden',
						boxShadow: '0 40px 100px rgba(0,0,0,0.7)',
						border: '1px solid rgba(255,255,255,0.07)',
					}}>
						{/* Header */}
						<div style={{
							padding: '0.9rem 1.2rem',
							borderBottom: '1px solid rgba(255,255,255,0.07)',
							display: 'flex', alignItems: 'center', justifyContent: 'space-between',
						}}>
							<div>
								<span style={{ ...syne, color: '#e8f0ff', fontWeight: 700, fontSize: '0.9rem' }}>
									🤳 Photo de profil instantanée
								</span>
								<p style={{ ...mono, fontSize: '0.62rem', color: '#5a6a9a', margin: '2px 0 0', letterSpacing: '0.05em' }}>
									Centrez votre visage dans le cadre
								</p>
							</div>
							<div style={{ display: 'flex', gap: '0.5rem' }}>
								<button onClick={flipCamera} title={facingMode === 'user' ? 'Caméra arrière' : 'Caméra avant'} style={{
									background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
									borderRadius: 8, width: 34, height: 34, cursor: 'pointer', color: '#c0cce8',
									display: 'flex', alignItems: 'center', justifyContent: 'center',
								}}>
									<FiRefreshCw size={15} />
								</button>
								<button onClick={closeCamera} aria-label="Fermer" style={{
									background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
									borderRadius: 8, width: 34, height: 34, cursor: 'pointer', color: '#c0cce8',
									display: 'flex', alignItems: 'center', justifyContent: 'center',
								}}>
									<FiX size={16} />
								</button>
							</div>
						</div>

						{/* Vidéo + overlay ovale */}
						<div style={{ position: 'relative', background: '#000', lineHeight: 0 }}>
							<video ref={videoRef} autoPlay playsInline muted
								style={{ width: '100%', display: 'block', maxHeight: 340, objectFit: 'cover' }} />
							<FaceOverlay />
						</div>

						{/* Footer */}
						<div style={{
							padding: '1rem 1.2rem',
							borderTop: '1px solid rgba(255,255,255,0.07)',
							display: 'flex', gap: '0.7rem', justifyContent: 'flex-end',
						}}>
							<button onClick={closeCamera} style={{
								...syne, padding: '0.62rem 1.1rem',
								background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
								borderRadius: 8, color: '#c0cce8', cursor: 'pointer',
								fontSize: '0.82rem', fontWeight: 600,
							}}>
								Annuler
							</button>
							<button onClick={handleCapture} style={{
								...syne, padding: '0.62rem 1.4rem',
								background: 'linear-gradient(135deg, #1a56f0, #0f3fd4)',
								border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer',
								fontSize: '0.85rem', fontWeight: 700,
								display: 'flex', alignItems: 'center', gap: 7,
								boxShadow: '0 4px 16px rgba(26,86,240,0.45)',
							}}>
								<FiCamera size={15} /> Capturer
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default Signup;
